import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Megaphone, RefreshCw, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'

import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { Switch } from '../../ui/Switch/Switch'
import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from '../onboarding/onboarding-api'
import type { OrganizationResponse } from '../onboarding/organization-onboarding-contracts'
import {
  readableAlertColor,
  type AlertTheme,
} from '../organization-alert/alert-color'
import {
  ALERT_MESSAGE_MAX_LENGTH,
  alertMessageLength,
  alertMessageProblem,
  normalizeAlertColor,
  normalizeAlertMessage,
  type AdministratorAlert,
} from '../organization-alert/organization-alert-contracts'
import { organizationAlertKeys } from '../organization-alert/organization-alert-queries'
import { SettingsConfirmDialog } from './SettingsConfirmDialog'
import { SettingsSaveFooter } from './SettingsSaveFooter'
import { canManageOrganizationAlert } from './settings-access'
import panel from './SettingsSection.module.css'
import styles from './OrganizationAlertSettings.module.css'

/** Design-system colors offered first; any other color is one click away. */
export const ALERT_COLOR_PRESETS = [
  { label: 'Red', value: '#aa3f4d' },
  { label: 'Amber', value: '#946416' },
  { label: 'Teal', value: '#16777a' },
  { label: 'Blue', value: '#2d6f91' },
  { label: 'Green', value: '#247657' },
  { label: 'Charcoal', value: '#1c2929' },
] as const
export const DEFAULT_ALERT_COLOR = ALERT_COLOR_PRESETS[0].value

type Draft = { message: string; color: string; active: boolean }

// A first alert is written to be shown; nothing is shown until it is saved.
function draftOf(alert: AdministratorAlert['alert']): Draft {
  return alert
    ? { message: alert.message, color: alert.color, active: alert.active }
    : { message: '', color: DEFAULT_ALERT_COLOR, active: true }
}

function sameDraft(first: Draft, second: Draft) {
  return (
    normalizeAlertMessage(first.message) ===
      normalizeAlertMessage(second.message) &&
    first.color === second.color &&
    first.active === second.active
  )
}

function saveError(error: unknown) {
  if (error instanceof OnboardingApiError) {
    if (error.code === 'revision-conflict')
      return 'Another administrator changed this alert. Reload to see the latest version.'
    if (error.code === 'forbidden')
      return 'Only an Organization Administrator with a verified email can change the alert.'
    if (error.code === 'unauthorized')
      return 'Sign in again to change the alert.'
    if (error.status === 400)
      return 'This message or color can’t be saved. Edit it and try again.'
  }
  return 'The alert couldn’t be saved. Nothing changed; try again.'
}

/**
 * Settings > Organization > Alert Text. Rendered for an Organization
 * Administrator only (never an Owner); the server enforces the same rule.
 */
export function OrganizationAlertSettings({
  organization,
  apiClient,
  notify,
}: {
  organization: OrganizationResponse
  apiClient?: OnboardingApiClient
  notify?: (kind: 'success' | 'error', message: string) => void
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const api = apiClient ?? getOnboardingApiClient()
  const organizationId = organization.organization.id
  const authorized =
    user !== null &&
    canManageOrganizationAlert(organization) &&
    !!api.getOrganizationAlert &&
    !!api.saveOrganizationAlert
  const queryKey = organizationAlertKeys.administrator(organizationId)
  const query = useQuery({
    queryKey,
    queryFn: () => api.getOrganizationAlert!(user!),
    enabled: authorized,
    retry: false,
    staleTime: 30_000,
  })
  // The revision this view just saved; the editor restarts on each revision.
  const [savedRevision, setSavedRevision] = useState<number | null>(null)

  if (!authorized) return null

  const verified =
    query.isSuccess && query.data.organizationId === organizationId
  return (
    <section className={panel.panel} aria-labelledby="organization-alert-title">
      <div className={panel.panelHeader}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>Alert Text</span>
          <h2 id="organization-alert-title">Organization alert</h2>
          <p>
            A short message shown in the header of every clinic in this
            organization, to everyone who uses them.
          </p>
        </div>
      </div>
      {query.isPending ? (
        <div className={styles.body} role="status">
          <span className={panel.srOnly}>Loading the organization alert</span>
          <Skeleton height="5rem" />
          <Skeleton height="2.5rem" width="60%" />
        </div>
      ) : !verified ? (
        <div className={panel.notice} data-tone="warning" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            <strong>The alert couldn’t be loaded safely</strong>
            <p>Nothing has changed. Try loading it again.</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void query.refetch()}
            >
              <RefreshCw size={14} aria-hidden="true" /> Retry
            </Button>
          </div>
        </div>
      ) : (
        <AlertEditor
          key={query.data.alert?.revision ?? 0}
          stored={query.data}
          api={api}
          saved={
            savedRevision !== null &&
            savedRevision === query.data.alert?.revision
          }
          onSaved={setSavedRevision}
          onReload={() => void query.refetch()}
          notify={notify}
        />
      )}
    </section>
  )
}

function AlertEditor({
  stored,
  api,
  saved,
  onSaved,
  onReload,
  notify,
}: {
  stored: AdministratorAlert
  api: OnboardingApiClient
  /** This revision is the one this view just saved. */
  saved: boolean
  onSaved: (revision: number | null) => void
  onReload: () => void
  notify?: (kind: 'success' | 'error', message: string) => void
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const queryClient = useQueryClient()
  const ids = {
    message: useId(),
    messageHint: useId(),
    color: useId(),
    active: useId(),
  }
  const initial = draftOf(stored.alert)
  const [draft, setDraft] = useState<Draft>(initial)
  const [clearing, setClearing] = useState(false)
  const dirty = !sameDraft(draft, initial)
  const problem = alertMessageProblem(draft.message, draft.active)
  // A problem is pointed out once something changed, not on a fresh form.
  const shownProblem = dirty ? problem : null
  const length = alertMessageLength(normalizeAlertMessage(draft.message))

  const save = useMutation({
    mutationFn: (next: Draft) =>
      api.saveOrganizationAlert!(user!, {
        expectedRevision: stored.alert?.revision ?? 0,
        active: next.active,
        message: normalizeAlertMessage(next.message),
        color: next.color,
      }),
    onSuccess: async (result, next) => {
      setClearing(false)
      onSaved(result.alert?.revision ?? null)
      queryClient.setQueryData(
        organizationAlertKeys.administrator(stored.organizationId),
        result
      )
      // Every clinic header reads the new alert at once for this user.
      await queryClient.invalidateQueries({
        queryKey: organizationAlertKeys.all,
        predicate: (query) => query.queryKey[1] === 'clinic',
      })
      notify?.(
        'success',
        !next.message
          ? 'Alert cleared.'
          : next.active
            ? 'Alert saved. Every clinic now shows it.'
            : 'Alert saved and turned off.'
      )
    },
  })
  const error = save.isError ? saveError(save.error) : null
  const conflict =
    save.error instanceof OnboardingApiError &&
    save.error.code === 'revision-conflict'

  function change(patch: Partial<Draft>) {
    save.reset()
    setDraft((current) => ({ ...current, ...patch }))
  }

  const hasStoredAlert = Boolean(stored.alert?.message || stored.alert?.active)
  const status = stored.alert?.active
    ? 'Showing on every clinic'
    : stored.alert?.message
      ? 'Turned off'
      : 'No alert'

  return (
    <>
      <div className={styles.body}>
        <p
          className={styles.status}
          data-active={stored.alert?.active || undefined}
        >
          <Megaphone size={14} aria-hidden="true" />
          {status}
        </p>

        <div className={styles.field}>
          <label htmlFor={ids.message} className={panel.fieldLabel}>
            Message
          </label>
          <textarea
            id={ids.message}
            className={styles.messageInput}
            value={draft.message}
            rows={3}
            maxLength={ALERT_MESSAGE_MAX_LENGTH * 2}
            disabled={save.isPending}
            aria-invalid={shownProblem ? true : undefined}
            aria-describedby={ids.messageHint}
            placeholder="For example: Our phone lines are down until 2 PM. Please text us instead."
            onChange={(event) => change({ message: event.currentTarget.value })}
          />
          <small id={ids.messageHint} className={styles.fieldFoot}>
            <span>Plain text on one line. Line breaks become spaces.</span>
            <span
              className={styles.counter}
              data-over={length > ALERT_MESSAGE_MAX_LENGTH || undefined}
            >
              {length}/{ALERT_MESSAGE_MAX_LENGTH}
            </span>
          </small>
        </div>

        <fieldset className={styles.colors} disabled={save.isPending}>
          <legend className={panel.fieldLabel}>Text color</legend>
          <div className={styles.swatches}>
            {ALERT_COLOR_PRESETS.map((preset) => (
              <label key={preset.value} className={styles.swatch}>
                <input
                  type="radio"
                  name="organization-alert-color"
                  value={preset.value}
                  checked={draft.color === preset.value}
                  onChange={() => change({ color: preset.value })}
                />
                <span
                  className={styles.swatchChip}
                  style={{ background: preset.value }}
                  aria-hidden="true"
                />
                <span className={styles.swatchLabel}>{preset.label}</span>
              </label>
            ))}
            <label className={styles.customColor} htmlFor={ids.color}>
              <input
                id={ids.color}
                type="color"
                value={draft.color}
                onChange={(event) => {
                  const color = normalizeAlertColor(event.currentTarget.value)
                  if (color) change({ color })
                }}
              />
              <span>Custom</span>
              <span className={styles.hex}>{draft.color}</span>
            </label>
          </div>
        </fieldset>

        <AlertPreview message={draft.message} color={draft.color} />

        <label className={styles.toggleRow} htmlFor={ids.active}>
          <span>
            <span className={panel.fieldLabel}>Show on every clinic</span>
            <span className={panel.hint}>
              Turn off to hide the alert and keep the message for later.
            </span>
          </span>
          <Switch
            id={ids.active}
            checked={draft.active}
            disabled={save.isPending}
            onChange={(event) =>
              change({ active: event.currentTarget.checked })
            }
          />
        </label>

        {conflict ? (
          <div className={styles.inlineNotice} role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              Another administrator changed this alert. Reload to see the latest
              version before you save.
            </span>
            <Button type="button" variant="secondary" onClick={onReload}>
              <RefreshCw size={14} aria-hidden="true" /> Reload
            </Button>
          </div>
        ) : null}
      </div>

      <SettingsSaveFooter
        dirty={dirty}
        saving={save.isPending && !clearing}
        saved={saved && !dirty}
        error={clearing ? null : error}
        problem={shownProblem}
        canSave={dirty && !problem && !save.isPending}
        saveLabel="Save alert"
        onDiscard={() => {
          save.reset()
          setDraft(initial)
        }}
        onSave={() => save.mutate(draft)}
        start={
          hasStoredAlert ? (
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending}
              onClick={() => setClearing(true)}
            >
              <Trash2 size={15} aria-hidden="true" /> Clear alert
            </Button>
          ) : null
        }
      />

      {clearing ? (
        <SettingsConfirmDialog
          title="Clear the organization alert?"
          confirmLabel="Clear alert"
          cancelLabel="Keep alert"
          danger
          busy={save.isPending}
          busyLabel="Clearing…"
          error={save.isError ? saveError(save.error) : null}
          onConfirm={() =>
            save.mutate({
              message: '',
              color: stored.alert?.color ?? draft.color,
              active: false,
            })
          }
          // A conflict stays on screen with its Reload after the dialog closes.
          onCancel={() => setClearing(false)}
        >
          The message is removed from every clinic’s header. You can write a new
          one at any time.
        </SettingsConfirmDialog>
      ) : null}
    </>
  )
}

function AlertPreview({ message, color }: { message: string; color: string }) {
  const text = normalizeAlertMessage(message) || 'Your message appears here.'
  const themes: AlertTheme[] = ['light', 'dark']
  const adjusted = themes.filter(
    (theme) => readableAlertColor(color, theme) !== color
  )
  return (
    <div className={styles.preview}>
      <span className={panel.fieldLabel}>Preview</span>
      <div className={styles.previewPair}>
        {themes.map((theme) => (
          <div
            key={theme}
            className={styles.previewBanner}
            data-preview-theme={theme}
          >
            <span className={styles.previewTheme}>
              {theme === 'light' ? 'Light mode' : 'Dark mode'}
            </span>
            <p style={{ color: readableAlertColor(color, theme) }}>
              <Megaphone size={14} aria-hidden="true" /> {text}
            </p>
          </div>
        ))}
      </div>
      {adjusted.length > 0 ? (
        <p className={panel.hint}>
          {adjusted.length === 2
            ? 'This color is shown as a readable shade in both modes.'
            : `This color is shown as a readable shade in ${adjusted[0]} mode.`}
        </p>
      ) : null}
    </div>
  )
}
